import { Cross } from "@/components/Icon/Cross"
import { usePromotionTag } from "@/lib/preFetch/prefetchPromoTag"
import {
    NextImage,
    PromotionalImage,
    useI18n,
    usePdp,
    usePdpImages,
    usePdpImagesMobile,
    usePrevNextButtons,
    useSwipeable,
} from "@jog/react-components"
import { ArrowIcon } from "@jog/react-components/src/components/Icon/Arrow"
import classNames from "classnames"
import { useState } from "react"
import InnerImageZoom from "react-inner-image-zoom"
import "react-inner-image-zoom/lib/InnerImageZoom/styles.min.css"

const MobileImageZoom = ({ image, index }: { index: number; image: { url: string; highest: string } }) => {
    const [close, setClose] = useState(false)
    const id = `image_zoom_${index}`
    return (
        <div className="relative w-full shrink-0 grow-0 basis-full">
            <InnerImageZoom
                hideCloseButton={true}
                zoomSrc={image.highest || image.url}
                src={image.url}
                className="w-full"
                imgAttributes={{ className: "w-full", id }}
                afterZoomIn={() => setClose(true)}
                afterZoomOut={() => setClose(false)}
            />
            {close ? (
                <button
                    aria-label="zoom out"
                    onClick={() => {
                        ;(document.querySelector(`#${id}`) as HTMLImageElement)?.click()
                    }}
                    className="absolute right-2.5 bottom-2.5 flex h-10 w-10 items-center justify-center bg-white/80">
                    <Cross className="h-6 w-6 fill-current text-grey-medium" />
                </button>
            ) : (
                <></>
            )}
        </div>
    )
}

export const Carousel = () => {
    const {
        detail: { desktopImages, mobileImages },
        productData,
    } = usePdp()
    const { $t } = useI18n()

    const desktop = useSwipeable(desktopImages.length)
    const [ref, emblaApi] = usePdpImages(desktopImages.length)
    const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi)

    const mobileCarousel = usePdpImagesMobile()
    const mobile = usePrevNextButtons(mobileCarousel[1])

    const promotionalBanner = productData?.promotionalBanner?.name?.[process.env.NEXT_PUBLIC_LOCALE!] || ""
    const promotionTag = usePromotionTag(promotionalBanner)
    const promotion = promotionTag ? (
        <PromotionalImage className="absolute right-3 top-4 z-[2] w-24 lg:right-4" promotionTag={promotionTag} />
    ) : (
        <></>
    )

    if (!desktopImages.length) {
        return <div>{$t("There are no images for this product")}</div>
    }
    return (
        <>
            <div id="desktopThumbnails" className="relative hidden h-full lg:block lg:p-[var(--carousel-space-out)]">
                <div className="sticky top-[var(--carousel-space-out)]">
                    <div
                        className={classNames(
                            "relative grid justify-between gap-x-[var(--carousel-space-x)]",
                            "lg:grid-cols-[var(--lg-slide-w),var(--lg-zoom-w)] lg:grid-rows-[var(--lg-zoom-h)]",
                            "xl:grid-cols-[var(--xl-slide-w),var(--xl-zoom-w)] xl:grid-rows-[var(--xl-zoom-h)]",
                            "2xl:grid-cols-[var(--xxl-slide-w),var(--xxl-zoom-w)] 2xl:grid-rows-[var(--xxl-zoom-h)]"
                        )}>
                        <div className="relative">
                            <div ref={ref} className="overflow-hidden">
                                <div className="-mt-[var(--carousel-space-y)] flex touch-pan-x flex-col lg:h-[var(--lg-slide-container-h)] xl:h-[var(--xl-slide-container-h)] 2xl:h-[var(--xxl-slide-container-h)]">
                                    {desktopImages.map((image, i) => (
                                        <div key={i} className="min-h-0 shrink-0 grow-0 pt-[var(--carousel-space-y)]">
                                            <div className="aspect-w-2 aspect-h-3" onClick={() => desktop.setActive(i)}>
                                                <NextImage
                                                    src={image.lowest || image.url}
                                                    alt="Thumbnail image"
                                                    width={60}
                                                    height={90}
                                                    className={classNames(
                                                        "cursor-pointer border hover:border-grey-medium lg:h-[var(--lg-slide-h)] xl:h-[var(--xl-slide-h)] 2xl:h-[var(--xxl-slide-h)]",
                                                        "h-full w-full object-cover",
                                                        i === desktop.active ? "border-grey-medium" : ""
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <button
                                    className="absolute -top-3.5 left-1/2 flex h-7 w-7 -translate-x-1/2 rotate-90 items-center justify-center border border-grey-medium bg-white hover:border-black disabled:hidden"
                                    type="button"
                                    disabled={prevBtnDisabled}
                                    onClick={onPrevButtonClick}
                                    aria-label="Previous">
                                    <ArrowIcon height={12} width={12} />
                                </button>
                                <button
                                    disabled={nextBtnDisabled}
                                    className="absolute -bottom-3.5 left-1/2 flex h-7 w-7 -translate-x-1/2 -rotate-90 items-center justify-center border border-grey-medium bg-white hover:border-black disabled:hidden"
                                    type="button"
                                    onClick={onNextButtonClick}
                                    aria-label="Next">
                                    <ArrowIcon height={12} width={12} />
                                </button>
                            </div>
                        </div>
                        <div className="relative z-[1] h-auto touch-pan-y">
                            <div className="flex">
                                <InnerImageZoom
                                    src={desktopImages?.[desktop.active]?.url}
                                    imgAttributes={{
                                        alt: desktopImages?.[desktop.active]?.label || "Product Image",
                                        className: classNames(
                                            "lg:!h-[var(--lg-zoom-h)] lg:!w-[var(--lg-zoom-w)]",
                                            "xl:!h-[var(--xl-zoom-h)] xl:!w-[var(--xl-zoom-w)]",
                                            "2xl:!h-[var(--xxl-zoom-h)] 2xl:!w-[var(--xxl-zoom-w)]",
                                            "object-contain"
                                        ),
                                    }}
                                    hideHint
                                    zoomSrc={
                                        desktopImages?.[desktop.active]?.highest || desktopImages?.[desktop.active]?.url
                                    }
                                />
                            </div>
                            {promotion}
                        </div>
                    </div>
                </div>
            </div>
            <div id="mobileThumbnails" className="relative block h-full lg:hidden">
                <div className="z-[1] grid h-auto touch-pan-y">
                    <div className="overflow-hidden" ref={mobileCarousel[0]}>
                        <div className="flex touch-pan-y touch-pinch-zoom">
                            {mobileImages.map((image, index) => (
                                <MobileImageZoom image={image} index={index} key={index} />
                            ))}
                        </div>
                    </div>
                    {promotion}
                </div>
                <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center justify-center gap-1">
                    {mobileImages.map((_, i) => (
                        <button
                            key={i}
                            className={classNames(
                                "h-1.5 w-1.5 cursor-pointer rounded-full",
                                i === mobile.selectedIndex ? "bg-white" : "bg-grey-light"
                            )}
                            onClick={() => mobile.onDotButtonClick(i)}
                            aria-label={`Image ${i + 1}`}
                        />
                    ))}
                </div>
            </div>
        </>
    )
}
